import React from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner, Alert, Form, InputGroup, Pagination, Modal } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faDownload, faEye, faFileExcel, faFilePdf, faArrowLeft, faExclamationTriangle, faSearch, faTimes, faPlus, faCalendarAlt, faSort, faSortUp, faSortDown, faChevronLeft, faChevronRight, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { useReports, useAttendance, useMeetSync } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import { Routes } from '../routes';
import { formatDateShort } from '../utils/dateUtils';
import Breadcrumbs from '../components/Breadcrumbs';
import EventDetailModal from '../components/modals/EventDetailModal';
import { EventsController } from '../core';

const Reports = () => {
  const { user } = useAuth();
  const history = useHistory();
  const reports = useReports();
  const attendance = useAttendance();
  const meetSync = useMeetSync();
  const [activeFilters, setActiveFilters] = React.useState([]);
  const [filterName, setFilterName] = React.useState('');
  const [filterDateType, setFilterDateType] = React.useState('single'); // 'single' o 'range'
  const [filterDate, setFilterDate] = React.useState('');
  const [filterDateFrom, setFilterDateFrom] = React.useState('');
  const [filterDateTo, setFilterDateTo] = React.useState('');
  
  // Estados para paginación y ordenamiento
  const [currentPage, setCurrentPage] = React.useState(1);
  const [sortField, setSortField] = React.useState('date');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const itemsPerPage = 10;
  
  // Estados para el modal de registro de asistencia
  const [showEventModal, setShowEventModal] = React.useState(false);
  const [selectedEventForAttendance, setSelectedEventForAttendance] = React.useState(null);
  const [modalActiveTab, setModalActiveTab] = React.useState('attendance');
  const [attendanceRegistered, setAttendanceRegistered] = React.useState(false);
  
  // Estados para el modal de confirmación de descarga
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [downloadEvent, setDownloadEvent] = React.useState(null);
  const [downloadFormat, setDownloadFormat] = React.useState(null);
  

  
  const handleBackToDashboard = () => {
    history.push(Routes.Dashboard.path);
  };
  
  // Filtrar eventos basado en los filtros activos
  const filteredEvents = React.useMemo(() => {
    return reports.reportEvents.filter(event => {
      const title = (event.summary || event.title || event.nombre || '').toLowerCase();
      const eventDate = event.start?.dateTime || event.start?.date || event.fecha_inicio;
      
      // Filtro por nombre
      if (activeFilters.includes('name') && filterName) {
        if (!title.includes(filterName.toLowerCase())) {
          return false;
        }
      }
      
      // Filtro por fecha
      if (activeFilters.includes('date') && eventDate) {
        // Crear fecha local sin conversión UTC para evitar problemas de zona horaria
        const eventDateObj = new Date(eventDate);
        const eventDateOnly = eventDateObj.getFullYear() + '-' + 
          String(eventDateObj.getMonth() + 1).padStart(2, '0') + '-' + 
          String(eventDateObj.getDate()).padStart(2, '0');
        
        if (filterDateType === 'single' && filterDate) {
          if (eventDateOnly !== filterDate) {
            return false;
          }
        } else if (filterDateType === 'range' && filterDateFrom && filterDateTo) {
          if (eventDateOnly < filterDateFrom || eventDateOnly > filterDateTo) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [reports.reportEvents, activeFilters, filterName, filterDateType, filterDate, filterDateFrom, filterDateTo]);
  
  const addFilter = (type) => {
    if (!activeFilters.includes(type)) {
      setActiveFilters([...activeFilters, type]);
    }
  };
  
  const removeFilter = (type) => {
    setActiveFilters(activeFilters.filter(f => f !== type));
    // Limpiar valores del filtro removido
    if (type === 'name') {
      setFilterName('');
    } else if (type === 'date') {
      setFilterDate('');
      setFilterDateFrom('');
      setFilterDateTo('');
      setFilterDateType('single');
    }
  };
  
  const clearAllFilters = () => {
    setActiveFilters([]);
    setFilterName('');
    setFilterDate('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDateType('single');
    setCurrentPage(1); // Reset pagination
  };
  
  // Función para manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset pagination when sorting
  };
  
  // Eventos ordenados
  const sortedEvents = React.useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = (a.summary || a.title || a.nombre || '').toLowerCase();
          bValue = (b.summary || b.title || b.nombre || '').toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.start?.dateTime || a.start?.date || a.fecha_inicio || 0);
          bValue = new Date(b.start?.dateTime || b.start?.date || b.fecha_inicio || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredEvents, sortField, sortDirection]);
  
  // Paginación
  const totalPages = Math.ceil(sortedEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEvents = sortedEvents.slice(startIndex, startIndex + itemsPerPage);
  
  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  React.useEffect(() => {
    if (user?.email) {
      reports.loadReportEvents(user.email);
    }
  }, [user?.email]);

  const handleViewReport = (event) => {
    // Validar permisos antes de navegar
    if (event.can_view_reports === false) {
      reports.setError('No tienes permisos para ver reportes de este evento');
      return;
    }
    
    const eventId = event.id || event.event_id;
    // Guardar datos del evento en localStorage para la vista previa
    localStorage.setItem('selectedEventForReport', JSON.stringify(event));
    // Navegar a la vista previa
    history.push(`/reports/preview/${eventId}`);
  };

  const handleDownloadReport = (event, format) => {
    // Validar permisos antes de mostrar modal
    if (event.can_view_reports === false) {
      reports.setError('No tienes permisos para descargar reportes de este evento');
      return;
    }
    
    setDownloadEvent(event);
    setDownloadFormat(format);
    setShowDownloadModal(true);
  };
  
  const confirmDownload = (soloAsistentes = true) => {
    const eventId = downloadEvent.id || downloadEvent.event_id;
    const eventTitle = downloadEvent.summary || downloadEvent.title || downloadEvent.nombre || 'Evento';
    
    if (soloAsistentes) {
      // Usar la función original para solo asistentes
      reports.downloadReport(eventId, downloadFormat, eventTitle);
    } else {
      // Usar URL personalizada para todos los invitados
      const url = `/eventos/${eventId}/reporte/descargar/${downloadFormat}/?solo_asistentes=false`;
      reports.downloadReportCustom(url, eventTitle, downloadFormat);
    }
    
    setShowDownloadModal(false);
    setDownloadEvent(null);
    setDownloadFormat(null);
  };
  
  const handleRegisterAttendance = async (event) => {
    // Transformar al formato esperado por el modal (igual que Dashboard)
    const transformedEvent = {
      id: event.google_event_id || event.event_id || event.id, // Usar google_event_id primero
      title: event.summary || event.title || event.nombre,
      summary: event.summary || event.title || event.nombre,
      start: event.start || new Date(event.fecha_inicio),
      end: event.end || new Date(event.fecha_fin),
      resource: {
        location: event.location || event.ubicacion || '',
        description: event.description || event.descripcion || ''
      }
    };
    
    setSelectedEventForAttendance(transformedEvent);
    setShowEventModal(true);
    setModalActiveTab('attendance');
    setAttendanceRegistered(false); // Reset flag
    meetSync.clearMeetData();
    
    // Cargar estado de Meet
    if (transformedEvent.id) {
      const eventDbId = await EventsController.getOrCreateEvent(transformedEvent.id, transformedEvent);
      if (eventDbId.success && eventDbId.data) {
        await meetSync.loadMeetStatus(eventDbId.data);
      }
    }
  };
  
  // Ejecutar validación cuando el modal se abre en attendance
  React.useEffect(() => {
    if (showEventModal && selectedEventForAttendance && modalActiveTab === 'attendance' && !attendanceRegistered) {
      // Callback para recargar eventos después del registro exitoso
      const onSuccessCallback = async () => {
        setAttendanceRegistered(true); // Marcar como registrado para evitar loop
        if (user?.email) {
          await reports.loadReportEvents(user.email);
        }
      };
      
      attendance.validateAndGenerateCredentials(
        selectedEventForAttendance.id,
        selectedEventForAttendance,
        user?.email,
        onSuccessCallback
      );
    }
  }, [showEventModal, selectedEventForAttendance, modalActiveTab, attendanceRegistered, user?.email, reports.loadReportEvents, attendance.validateAndGenerateCredentials]);
  
  // Handlers para sincronización de Meet
  const handleSyncMeet = React.useCallback(async () => {
    if (!selectedEventForAttendance?.id) return;
    meetSync.setSyncing(true);
    
    const result = await EventsController.getOrCreateEvent(selectedEventForAttendance.id, selectedEventForAttendance);
    if (result.success && result.data) {
      await meetSync.syncMeet(result.data, false);
      if (user?.email) {
        await reports.loadReportEvents(user.email);
      }
    } else {
      meetSync.setSyncing(false);
    }
  }, [selectedEventForAttendance, meetSync, user?.email, reports]);

  const handleResyncMeet = React.useCallback(async () => {
    if (!selectedEventForAttendance?.id) return;
    meetSync.setSyncing(true);
    
    const result = await EventsController.getOrCreateEvent(selectedEventForAttendance.id, selectedEventForAttendance);
    if (result.success && result.data) {
      await meetSync.syncMeet(result.data, true);
      if (user?.email) {
        await reports.loadReportEvents(user.email);
      }
    } else {
      meetSync.setSyncing(false);
    }
  }, [selectedEventForAttendance, meetSync, user?.email, reports]);

  return (
    <Container fluid className="px-4">
      <Row className="justify-content-md-center">
        <Col xs={12}>
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
            <div className="d-block mb-4 mb-md-0">
              <Breadcrumbs />
              <h2 className="h4">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Reportes de Asistencia
              </h2>
              <p className="mb-0">Genera y descarga reportes de asistencia de eventos</p>
              {user?.dependencia && (
                <div className="mt-2">
                  <small className="text-muted">
                    <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
                    Mostrando eventos de: <strong>{user.dependencia}</strong>
                  </small>
                </div>
              )}
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Button 
                variant="outline-secondary" 
                onClick={handleBackToDashboard}
                className="d-flex align-items-center"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Regresar
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card>
            <Card.Body>
              {/* Selector inicial de filtros */}
              {activeFilters.length === 0 && (
                <Row>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Filtrar</Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button 
                          variant="outline-primary" 
                          onClick={() => addFilter('name')}
                        >
                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                          Por nombre del evento
                        </Button>
                        <Button 
                          variant="outline-info" 
                          onClick={() => addFilter('date')}
                        >
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                          Por fecha
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              
              {/* Filtros activos */}
              {activeFilters.length > 0 && (
                <>
                  {/* Filtro por nombre */}
                  {activeFilters.includes('name') && (
                    <Row className="mb-3">
                      <Col xs={12} md={8}>
                        <Form.Group>
                          <Form.Label className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faSearch} className="me-2" />
                            Buscar por nombre
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="ms-2 p-0 text-danger"
                              onClick={() => removeFilter('name')}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Escriba el nombre del evento..."
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Filtro por fecha */}
                  {activeFilters.includes('date') && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            Filtrar por fecha
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="ms-2 p-0 text-danger"
                              onClick={() => removeFilter('date')}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </Form.Label>
                          <Row>
                            <Col xs={12} md={3}>
                              <Form.Select 
                                value={filterDateType} 
                                onChange={(e) => setFilterDateType(e.target.value)}
                              >
                                <option value="single">Fecha específica</option>
                                <option value="range">Rango de fechas</option>
                              </Form.Select>
                            </Col>
                            {filterDateType === 'single' ? (
                              <Col xs={12} md={4}>
                                <Form.Control
                                  type="date"
                                  value={filterDate}
                                  onChange={(e) => setFilterDate(e.target.value)}
                                />
                              </Col>
                            ) : (
                              <>
                                <Col xs={12} md={3}>
                                  <Form.Control
                                    type="date"
                                    placeholder="Desde"
                                    value={filterDateFrom}
                                    onChange={(e) => setFilterDateFrom(e.target.value)}
                                  />
                                </Col>
                                <Col xs={12} md={3}>
                                  <Form.Control
                                    type="date"
                                    placeholder="Hasta"
                                    value={filterDateTo}
                                    onChange={(e) => setFilterDateTo(e.target.value)}
                                  />
                                </Col>
                              </>
                            )}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Botón para agregar más filtros */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        {!activeFilters.includes('name') && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => addFilter('name')}
                          >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Nombre
                          </Button>
                        )}
                        {!activeFilters.includes('date') && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => addFilter('date')}
                          >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Fecha
                          </Button>
                        )}
                        
                        <div className="ms-auto d-flex gap-2 align-items-center">
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={clearAllFilters}
                          >
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Limpiar todo
                          </Button>
                          <small className="text-muted">
                            {filteredEvents.length} de {reports.reportEvents.length} eventos
                            {activeFilters.length > 0 && (
                              <span className="text-primary ms-1">(filtrado)</span>
                            )}
                          </small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Reportes Disponibles</h5>
              <small className="text-muted">Usuario: {user?.email}</small>
            </Card.Header>
            <Card.Body>
              {reports.loadingReports && (
                <div className="text-center mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando eventos...
                </div>
              )}

              {sortedEvents.length > 0 && (
                <>
                  <Table responsive bordered hover>
                    <thead>
                      <tr>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('name')}
                          className="align-middle"
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Nombre del Evento</span>
                            <FontAwesomeIcon 
                              icon={
                                sortField === 'name' 
                                  ? (sortDirection === 'asc' ? faSortUp : faSortDown)
                                  : faSort
                              } 
                              className="ms-1 text-muted"
                            />
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('date')}
                          className="align-middle"
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Fecha</span>
                            <FontAwesomeIcon 
                              icon={
                                sortField === 'date' 
                                  ? (sortDirection === 'asc' ? faSortUp : faSortDown)
                                  : faSort
                              } 
                              className="ms-1 text-muted"
                            />
                          </div>
                        </th>
                        <th className="text-center">Registrar Asistencia</th>
                        <th className="text-center">Ver Informe</th>
                        <th className="text-center">Descargar Excel</th>
                        <th className="text-center">Descargar PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEvents.map((event, index) => {
                      const title = event.summary || event.title || event.nombre || 'Sin título';
                      const startDate = event.start?.dateTime || event.start?.date || event.fecha_inicio;
                      const canViewReports = event.can_view_reports !== false && event.can_view_reports !== 'false';
                      const accessLevel = event.access_level || 'full'; // Por defecto full si no está definido
                      
                      return (
                        <tr key={index} className={`event-card ${accessLevel === 'full' ? 'full-access' : 'limited-access'}`}>
                          <td>{title}</td>
                          <td>
                            {startDate ? formatDateShort(startDate) : 'No especificado'}
                          </td>
                          <td className="text-center">
                            <Button 
                              variant="warning" 
                              size="sm"
                              onClick={() => handleRegisterAttendance(event)}
                              disabled={reports.loadingReports}
                            >
                              <FontAwesomeIcon icon={faPlus} className="me-1" />
                              Registrar
                            </Button>
                          </td>
                          <td className="text-center">
                            {canViewReports ? (
                              <Button 
                                variant="primary" 
                                size="sm"
                                onClick={() => handleViewReport(event)}
                                disabled={reports.loadingReports}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Ver
                              </Button>
                            ) : (
                              <span title="No tienes permisos para ver reportes de este evento">
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  disabled
                                  style={{ pointerEvents: 'none' }}
                                >
                                  <FontAwesomeIcon icon={faEye} className="me-1" />
                                  Ver
                                </Button>
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            {canViewReports ? (
                              <Button 
                                size="sm"
                                onClick={() => handleDownloadReport(event, 'xlsx')}
                                disabled={reports.loadingReports}
                                style={{
                                  backgroundColor: '#217346',
                                  borderColor: '#217346',
                                  color: 'white'
                                }}
                              >
                                <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                                Excel
                              </Button>
                            ) : (
                              <span title="No tienes permisos para descargar reportes de este evento">
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  disabled
                                  style={{ pointerEvents: 'none' }}
                                >
                                  <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                                  Excel
                                </Button>
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            {canViewReports ? (
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleDownloadReport(event, 'pdf')}
                                disabled={reports.loadingReports}
                              >
                                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                PDF
                              </Button>
                            ) : (
                              <span title="No tienes permisos para descargar reportes de este evento">
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  disabled
                                  style={{ pointerEvents: 'none' }}
                                >
                                  <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                  PDF
                                </Button>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </Table>
                  
                  {/* Paginación */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <small className="text-muted">
                          Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, sortedEvents.length)} de {sortedEvents.length} eventos
                        </small>
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev 
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </Pagination.Prev>
                        
                        {getPageNumbers().map(pageNum => (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        ))}
                        
                        <Pagination.Next 
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </Pagination.Next>
                      </Pagination>
                    </div>
                  )}
                </>
              )}

              {!reports.loadingReports && reports.reportEvents.length === 0 && (
                <Alert variant="info">
                  <strong>No hay eventos con asistencia registrada.</strong>
                  <br />
                  Para generar reportes, primero debes registrar la asistencia de los invitados en los eventos desde el Dashboard.
                </Alert>
              )}
              
              {!reports.loadingReports && reports.reportEvents.length > 0 && filteredEvents.length === 0 && (
                <Alert variant="warning">
                  No se encontraron eventos que coincidan con los filtros aplicados.
                </Alert>
              )}

              {reports.error && (
                <Alert variant="danger" className="mt-3">
                  {reports.error}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Modal de Registro de Asistencia */}
      <EventDetailModal
        show={showEventModal}
        onHide={() => {
          setShowEventModal(false);
          setSelectedEventForAttendance(null);
          setModalActiveTab('attendance');
          setAttendanceRegistered(false);
          attendance.clearAttendanceData();
          meetSync.clearMeetData();
        }}
        selectedEvent={selectedEventForAttendance}
        activeTab={modalActiveTab}
        onTabSelect={(tab) => {
          setModalActiveTab(tab);
        }}
        validatingGuests={attendance.validatingGuests}
        generatingAttendance={attendance.generatingAttendance}
        validationResult={attendance.validationResult}
        attendanceQR={attendance.attendanceQR}
        attendanceLink={attendance.attendanceLink}
        loadingReports={false}
        error={attendance.error}
        htmlContent={null}
        onViewEventReport={() => {}}
        onDownloadEventReport={() => {}}
        onDownloadQR={attendance.downloadQR}
        onCopyLink={attendance.copyToClipboard}
        hideReportsTab={true}
        hideInfoTab={true}
        meetStatus={meetSync.meetStatus}
        loadingMeetStatus={meetSync.loadingStatus}
        syncingMeet={meetSync.syncing}
        meetSyncResult={meetSync.syncResult}
        meetError={meetSync.error}
        onSyncMeet={handleSyncMeet}
        onResyncMeet={handleResyncMeet}
      />
      
      {/* Modal de Confirmación de Descarga */}
      <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Tipo de Reporte</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Qué tipo de reporte deseas descargar?</p>
          <div className="d-grid gap-2">
            <Button 
              variant="outline-primary" 
              onClick={() => confirmDownload(true)}
              className="text-start"
            >
              <strong>Solo Asistentes</strong>
              <br />
              <small className="text-muted">Incluye únicamente las personas que confirmaron asistencia</small>
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={() => confirmDownload(false)}
              className="text-start"
            >
              <strong>Todos los Invitados</strong>
              <br />
              <small className="text-muted">Incluye todos los invitados (asistieron o no)</small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDownloadModal(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Reports;